import { Download } from 'lucide-react'
import { useEffect } from 'react'
import { api, type ImpactTarget } from '../api'
import { useApp } from '../context'
import { useAsync } from '../hooks'
import { PanelShell } from './PanelShell'
import { Button, Callout, Empty, ErrorState, Loading, Section, Stat } from './ui'

function targetLabel(t: ImpactTarget, name: (id: string) => string, cableName: (id: string) => string) {
  if (t.kind === 'cable') return `Cut on ${cableName(t.cableId)}`
  if (t.kind === 'node') return `Failure at ${name(t.nodeId)}`
  return `PON ${t.port} down`
}

export function ImpactPanel({ target }: { target: ImpactTarget }) {
  const { open, setHighlight, nodeName, network } = useApp()
  const key = JSON.stringify(target)
  const q = useAsync(() => api.impact(target), [key])
  const cableName = (id: string) => network.cables.find((c) => c.id === id)?.name ?? id

  useEffect(() => {
    if (!q.data) return
    setHighlight({
      cableIds: target.kind === 'cable' ? [target.cableId] : [],
      nodeIds: [...q.data.byNap.map((n) => n.nodeId), ...(target.kind === 'node' ? [target.nodeId] : [])],
      tone: 'danger',
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.data, setHighlight])

  if (!q.data) return q.error ? <ErrorState error={q.error} onRetry={q.reload} /> : <Loading label="Tracing every subscriber…" />
  const r = q.data

  const exportCsv = () => {
    const rows = [['account_ref', 'label', 'nap'], ...r.services.map((s) => [s.accountRef, s.label, nodeName(s.napNodeId)])]
    const csv = rows.map((row) => row.map((v) => `"${v.replace(/"/g, '""')}"`).join(',')).join('\n')
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }))
    const a = document.createElement('a')
    a.href = url
    a.download = `impact-${target.kind}-${Date.now()}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <PanelShell
      title={targetLabel(target, nodeName, cableName)}
      subtitle="Simulation from recorded connectivity. Nothing is changed on the network."
      actions={
        r.services.length ? (
          <Button size="sm" onClick={exportCsv}>
            <Download className="h-4 w-4" aria-hidden /> Export affected list (CSV)
          </Button>
        ) : undefined
      }
    >
      <Section title="Scope">
        <div className="grid grid-cols-2 gap-2">
          <Stat label="Subscribers without service" value={r.affectedServiceIds.length} tone={r.affectedServiceIds.length ? 'danger' : 'ok'} />
          <Stat label="NAPs affected" value={r.byNap.length} sub={`${r.byPon.length} PON port${r.byPon.length === 1 ? '' : 's'}`} />
        </div>
        {r.untraceableServiceIds.length > 0 && (
          <div className="mt-3">
            <Callout tone="warn" title={`${r.untraceableServiceIds.length} subscribers could not be evaluated`}>
              Their paths do not reach an OLT in the records, so they may or may not be affected. Fix them in Data quality.
            </Callout>
          </div>
        )}
      </Section>
      {r.byPon.length > 0 && (
        <Section title="By PON port">
          <ul className="divide-y divide-border text-sm">
            {r.byPon.map((p) => (
              <li key={`${p.oltId}:${p.port}`} className="flex justify-between py-1.5">
                <span>{p.label}</span>
                <span className="num font-medium">{p.count}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}
      <Section title="By NAP">
        {!r.byNap.length && <Empty title="No subscribers depend on this">It is safe to work on from a service point of view.</Empty>}
        <ul className="divide-y divide-border text-sm">
          {r.byNap.map((n) => (
            <li key={n.nodeId}>
              <button className="flex w-full justify-between py-1.5 text-left hover:text-accent" onClick={() => open({ kind: 'node', id: n.nodeId })}>
                <span>{n.name}</span>
                <span className="num font-medium">{n.count}</span>
              </button>
            </li>
          ))}
        </ul>
      </Section>
    </PanelShell>
  )
}
